import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { GCOrderItemConsole, GCParcel, GCParcelGroup } from '../models/agrorendement-genconsole.model';
import { Farm } from '../models/farm.model';
import { Page } from '../models/page.model';
import { HttpWanaka } from '../utils/http.wanaka';
import { isGenConsoleSupported } from '../pages/tool/agrorendement/common/utils';

@Injectable({ providedIn: 'root' })
export class ARGenConsoleService {
  // FIXME culture => oad
  protected baseUrl = '/tools/agrorendement/results/genconsole/{culture}';
  protected pdfSummaryUrl = '/tools/agrorendement/results/genconsole/summary/genfiles';

  private farmsUrl = '/farms';
  private dosesUrl = '/doses';
  private consolesUrl = '/consoles';
  private genUrl = '/genfiles';

  constructor(private http: HttpClient) {}

  getCultureUrl(culture: string) {
    return this.baseUrl.replace('{culture}', culture);
  }

  getFarmsAdvanced(
    culture: string,
    pageIndex: number,
    itemsPerPage: number,
    sortColumnName: string,
    sortColumnOrder: string,
    filter: any,
  ): Observable<Page<Farm>> {
    const gcFarmsUrl = this.getCultureUrl(culture) + this.farmsUrl;
    let url = gcFarmsUrl + '?size=' + itemsPerPage + '&page=' + (pageIndex - 1);
    if (filter.filter) {
      url += '&prefix=' + encodeURIComponent(filter.filter.trim());
    }
    if (filter.organisation) {
      url += '&organisation=' + encodeURIComponent(filter.organisation);
    }
    if (sortColumnName) {
      url += '&sort=' + sortColumnName + ',' + sortColumnOrder;
    }
    return this.http.get<any>(url).pipe(
      map((response) => response.data),
      catchError(this.handleError),
    );
  }

  getParcelsForFarms(culture: string, farmIds: number[]): Observable<GCParcel[]> {
    const gcdosesUrl = this.getCultureUrl(culture) + this.dosesUrl;
    const url = gcdosesUrl + '?farms=' + farmIds.join(',');
    return this.http.get<any>(url).pipe(
      map((response) => response.data),
      catchError(this.handleError),
    );
  }

  updateDoses(culture: string, parcels: GCParcel[]): Observable<any> {
    const gcdosesUrl = this.getCultureUrl(culture) + this.dosesUrl;
    const url = gcdosesUrl;

    return this.http.post(url, parcels).pipe(catchError(this.handleError));
  }

  getConsolesForParcels(culture: string, orderItemIds: number[]): Observable<GCOrderItemConsole[]> {
    const gcConsolesUrl = this.getCultureUrl(culture) + this.consolesUrl;
    const url = gcConsolesUrl + '?orderItems=' + orderItemIds.join(',');
    return this.http.get<any>(url).pipe(
      map((response) => response.data),
      catchError(this.handleError),
    );
  }

  updateConsoles(culture: string, consoles: GCOrderItemConsole[]): Observable<any> {
    const gcConsolesUrl = this.getCultureUrl(culture) + this.consolesUrl;
    const url = gcConsolesUrl;
    return this.http.post(url, consoles).pipe(catchError(this.handleError));
  }

  generateConsoleSummary(sessionId: string, farmIds: number[], userId: string): Observable<any> {
    const payload = { sessionId: sessionId, farmIds: farmIds, userId: userId };
    // observe: 'response' so the component can check response.ok (legacy Http semantics)
    return this.http.post(this.pdfSummaryUrl, payload, { observe: 'response' }).pipe(catchError(this.handleError));
  }

  saveFile(blob: Blob, filename: string) {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a') as HTMLAnchorElement; // Use type assertion
    document.body.appendChild(a);
    a.style.display = 'none';
    a.href = url;
    (a as any).download = filename; // Type assertion to bypass the TypeScript error
    a.click();
    window.URL.revokeObjectURL(url);
    a.remove();
  }

  generateConsoleFiles(
    culture: string,
    parcels: GCParcel[],
    groups: GCParcelGroup[],
    originalZonageIds: Set<number>,
  ): Observable<any> {
    const parcelIdsWithGCUnsupported = this.parcelIdsWithGenConsoleUnsupported(parcels);
    groups = groups.filter((value) => {
      return !parcelIdsWithGCUnsupported.has(value.parcelId);
    });

    // vers array
    const originalZonage = new Array<number>();
    originalZonageIds.forEach((x) => {
      originalZonage.push(x);
    });

    const genUrl = this.getCultureUrl(culture) + this.genUrl;
    const url = genUrl;

    const arg: any = {};
    arg.groups = groups;
    arg.originalZonage = originalZonage;

    return this.http.post(url, arg).pipe(catchError(this.handleError));
  }

  private handleError(error: any) {
    // FIXME
    console.error('Une erreur est survenue', error);
    return throwError(() => error.message || error);
  }

  parcelIdsWithGenConsoleUnsupported(parcels: GCParcel[]) {
    const unsupportedParcelIds = new Set<number>();
    for (const parcel of parcels) {
      if (!isGenConsoleSupported(parcel.console)) {
        unsupportedParcelIds.add(parcel.id);
      }
    }
    return unsupportedParcelIds;
  }
}
